import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash, FaFilter } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import { toTitleCase } from '../../../utils/formHelpers';
import './AddDepartment.css';

const EMPTY_FORM = { name: '' };

const AddDepartment = () => {
  const { user } = useAuth();
  const { colleges, loading: collegesLoading } = useColleges();
  const isScoped = user?.role === 'collegeadmin' || user?.role === 'clerk';

  const [departments, setDepartments]       = useState([]);
  const [loading, setLoading]               = useState(false);
  const [saving, setSaving]                 = useState(false);

  // Controls & Filters
  const [searchQuery, setSearchQuery]       = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [filterCollege, setFilterCollege]   = useState('');

  // Form State
  const [showAddForm, setShowAddForm]       = useState(false);
  const [formName, setFormName]             = useState('');
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [editId, setEditId]                 = useState(null);
  const [expandedRow, setExpandedRow]       = useState(null);

  // College dropdown in form
  const [collegeSearch, setCollegeSearch]   = useState('');
  const [showCollegeDrop, setShowCollegeDrop] = useState(false);
  const collegeDropRef                      = useRef(null);
  const searchRef                           = useRef(null);

  // ── Load Data ──────────────────────────────────────────────
  const loadData = async () => {
    setLoading(true);
    try {
      const deptRes = await api.get('/departments');
      setDepartments(deptRes.data.data || []);
    } catch (err) {
      Swal.fire('Error', 'Failed to load data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (colleges.length === 1) setSelectedColleges([colleges[0]._id]);
  }, [colleges]);

  useEffect(() => {
    const handler = (e) => {
      if (collegeDropRef.current && !collegeDropRef.current.contains(e.target)) setShowCollegeDrop(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredCollegeOptions = useMemo(() => {
    if (!collegeSearch) return colleges;
    const q = collegeSearch.toLowerCase();
    return colleges.filter(c => c.name.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q));
  }, [colleges, collegeSearch]);

  const allSelected = selectedColleges.length === colleges.length && colleges.length > 0;

  const toggleCollege = (id) => {
    setSelectedColleges(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedColleges(allSelected ? [] : colleges.map(c => c._id));
  };

  const selectedCollegeNames = useMemo(() => {
    if (selectedColleges.length === 0) return '-- Select College(s) --';
    if (selectedColleges.length === colleges.length && colleges.length > 0) return 'All Colleges';
    return colleges.filter(c => selectedColleges.includes(c._id)).map(c => c.name).join(', ');
  }, [selectedColleges, colleges]);

  const validateForm = () => {
    if (!formName.trim()) {
      Swal.fire('Missing Field', 'Please enter the department name.', 'warning');
      return false;
    }
    if (!isScoped && selectedColleges.length === 0) {
      Swal.fire('Missing Field', 'Please select at least one college.', 'warning');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setFormName('');
    setSelectedColleges(colleges.length === 1 ? [colleges[0]._id] : []);
    setEditId(null);
    setShowAddForm(false);
    setCollegeSearch('');
    setShowCollegeDrop(false);
  };

  // ── Save (Create / Update) ─────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editId) {
        const collegeId = isScoped
          ? (user?.college?._id || user?.college)
          : selectedColleges[0];
        const { data } = await api.put(`/departments/${editId}`, { name: formName, college: collegeId });
        const collegeName = colleges.find(c => c._id === collegeId)?.name;
        const updated = { ...data.data, college: { _id: collegeId, name: collegeName || data.data.college?.name } };
        setDepartments(prev => prev.map(d => d._id === editId ? updated : d));
        Swal.fire('Updated!', 'Department has been updated.', 'success');
        resetForm();
      } else {
        const collegeIds = isScoped
          ? [user?.college?._id || user?.college]
          : selectedColleges;
        await Promise.all(collegeIds.map(collegeId => api.post('/departments', { name: formName, college: collegeId })));
        Swal.fire('Added!', `Created ${collegeIds.length} department row(s).`, 'success');
        resetForm();
        loadData();
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save department.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit & Delete ──────────────────────────────────────────
  const handleEdit = (dept) => {
    setEditId(dept._id);
    setFormName(dept.name);
    setSelectedColleges([dept.college?._id || dept.college || '']);
    setShowAddForm(true);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (dept) => {
    Swal.fire({
      title: `Delete "${dept.name}"?`,
      text: 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      confirmButtonText: 'Yes, delete it',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await api.delete(`/departments/${dept._id}`);
        setDepartments(prev => prev.filter(d => d._id !== dept._id));
        Swal.fire('Deleted!', 'Department has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
      }
    });
  };

  const toggleStatus = async (dept) => {
    try {
      const newStatus = !dept.isActive;
      await api.patch(`/departments/${dept._id}/toggle-status`);
      setDepartments(prev => prev.map(d => d._id === dept._id ? { ...d, isActive: newStatus } : d));
      Swal.fire({
        title: 'Status Updated!',
        text: `Department is now ${newStatus ? 'Active' : 'Inactive'}.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to update status.', 'error');
    }
  };

  // ── Search & Filter ────────────────────────────────────────
  const displayedDepartments = useMemo(() => {
    let result = departments;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(d => d.name.toLowerCase().includes(q));
    }

    if (filterCollege) {
      result = result.filter(d => String(d.college?._id || d.college) === filterCollege);
    }

    return result;
  }, [departments, searchQuery, filterCollege]);

  return (
    <div className="add-department-for-employee-container">
      <h2 className="add-department-for-employee-title">Employee Departments</h2>

      {/* ── Controls ── */}
      <div className="add-department-for-employee-controls">
        
        <div className="add-department-for-employee-search-wrapper" ref={searchRef}>
          <FaSearch className="add-department-for-employee-search-icon" />
          <input
            type="text"
            className="add-department-for-employee-search-input"
            placeholder="Search by department name..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
            onFocus={() => setShowSearchDropdown(true)}
          />
          {showSearchDropdown && searchQuery && (
            <div className="add-department-for-employee-search-dropdown">
              <ul>
                {displayedDepartments.length > 0 ? displayedDepartments.map(d => (
                  <li key={d._id} onClick={() => { setSearchQuery(d.name); setShowSearchDropdown(false); }}>
                    {d.name}
                  </li>
                )) : (
                  <div className="add-department-for-employee-search-empty">No departments found</div>
                )}
              </ul>
            </div>
          )}
        </div>

        {!isScoped && colleges.length > 1 && (
          <select
            className="add-department-for-employee-filter-select"
            value={filterCollege}
            onChange={e => setFilterCollege(e.target.value)}
          >
            <option value="">All Colleges</option>
            {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        )}

        <button
          className="add-department-for-employee-btn add-department-for-employee-btn-add"
          onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}
        >
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Department'}
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showAddForm && (
        <div className="add-department-for-employee-inline-form">
          <h3 className="add-department-for-employee-form-title">
            {editId ? '✏️ Edit Department' : 'Add New Department'}
          </h3>

          <div className="add-department-for-employee-form-grid">

            {/* College */}
            <div className="add-department-for-employee-form-group">
              <label>College <span className="add-department-for-employee-req">*</span></label>
              {isScoped || colleges.length === 1 ? (
                <input
                  value={colleges[0]?.name || 'Loading...'}
                  disabled
                  style={{ background: '#f1f5f9', padding: '9px 11px', border: '1px solid #ccc', borderRadius: '6px' }}
                />
              ) : (
                <div className="cls-college-dropdown-wrapper" ref={collegeDropRef}>
                  <div className="cls-college-select-box" onClick={() => setShowCollegeDrop(v => !v)}>
                    <span style={{ color: selectedColleges.length ? '#1e293b' : '#94a3b8', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedCollegeNames}
                    </span>
                    <FaChevronDown style={{ fontSize: '0.75rem', color: '#64748b', flexShrink: 0 }} />
                  </div>
                  {showCollegeDrop && (
                    <div className="cls-college-dropdown">
                      <div className="cls-college-search">
                        <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem' }} />
                        <input type="text" placeholder="Search college..." value={collegeSearch} onChange={e => setCollegeSearch(e.target.value)} autoFocus />
                      </div>
                      {!collegeSearch && !editId && (
                        <div className="cls-college-option cls-select-all" onClick={toggleSelectAll}>
                          <input type="checkbox" checked={allSelected} readOnly />
                          <span>Select All</span>
                        </div>
                      )}
                      <div className="cls-college-options">
                        {filteredCollegeOptions.map(c => (
                          <div key={c._id} className="cls-college-option" onClick={() => editId ? setSelectedColleges([c._id]) : toggleCollege(c._id)}>
                            <input type={editId ? 'radio' : 'checkbox'} checked={selectedColleges.includes(c._id)} readOnly />
                            <span>{c.name}</span>
                            {c.code && <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 'auto' }}>{c.code}</span>}
                          </div>
                        ))}
                        {filteredCollegeOptions.length === 0 && (
                          <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '0.85rem' }}>No colleges found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="add-department-for-employee-form-group">
              <label>Department Name <span className="add-department-for-employee-req">*</span></label>
              <input type="text" value={formName} onChange={e => setFormName(toTitleCase(e.target.value))} placeholder="e.g. Computer Science" />
            </div>

          </div>

          {!editId && selectedColleges.length > 1 && (
            <div style={{ marginBottom: 14, padding: '10px 14px', background: '#eff6ff', borderRadius: 6, fontSize: '0.82rem', color: '#2563eb' }}>
              ℹ️ Will create <strong>{selectedColleges.length} separate rows</strong> (one per college)
            </div>
          )}

          <div className="add-department-for-employee-form-buttons">
            <button className="add-department-for-employee-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="add-department-for-employee-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Department' : 'Save Department'}
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading departments...</div>
      ) : (
        <div className="add-department-for-employee-table-wrapper">
          <table className="add-department-for-employee-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th className="add-department-for-employee-align-left">Department Name</th>
                <th className="add-dept-table-college">College</th>
                <th className="add-dept-table-status">Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedDepartments.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No departments found.</td></tr>
              ) : displayedDepartments.map((dept, i) => (
                <React.Fragment key={dept._id}>
                  <tr className="add-department-for-employee-main-row">
                    <td>{i + 1}</td>
                    <td className="add-department-for-employee-align-left">
                      <div
                        className="add-department-for-employee-name-wrapper"
                        onClick={() => setExpandedRow(expandedRow === dept._id ? null : dept._id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span>{dept.name}</span>
                        <button className="add-department-for-employee-expand-btn">
                          {expandedRow === dept._id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>
                    <td className="add-dept-table-college">
                      <span className="add-department-for-employee-badge">{dept.college?.name || '—'}</span>
                    </td>
                    <td className="add-dept-table-status">
                      <label className="add-department-for-employee-switch">
                        <input type="checkbox" checked={dept.isActive} onChange={() => toggleStatus(dept)} />
                        <span className="add-department-for-employee-slider" />
                      </label>
                      <div style={{ fontSize: '0.7rem', marginTop: '4px', color: dept.isActive ? '#27ae60' : '#e74c3c', fontWeight: '600' }}>
                        {dept.isActive ? 'Active' : 'Disabled'}
                      </div>
                    </td>
                    <td>
                      <div className="add-department-for-employee-action-btns">
                        <button className="add-department-for-employee-btn-edit" onClick={() => handleEdit(dept)} title="Edit"><FaEdit /></button>
                        <button className="add-department-for-employee-btn-delete" onClick={() => handleDelete(dept)} title="Delete"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedRow === dept._id && (
                    <tr className="add-department-for-employee-expanded-row">
                      <td colSpan="5">
                        <div className="add-department-for-employee-expanded-content">
                          
                          {/* College — show in expand ONLY when hidden from table (< 768px) */}
                          <div className="add-department-for-employee-expanded-item add-dept-exp-college">
                            <span className="add-department-for-employee-exp-label">College</span>
                            <span className="add-department-for-employee-exp-value">{dept.college?.name || '—'}</span>
                          </div>

                          {/* Status — show in expand ONLY when hidden from table (< 480px) */}
                          <div className="add-department-for-employee-expanded-item add-dept-exp-status">
                            <span className="add-department-for-employee-exp-label">Status</span>
                            <span className="add-department-for-employee-exp-value" style={{ color: dept.isActive ? '#27ae60' : '#e74c3c', fontWeight: 700 }}>
                              {dept.isActive ? 'Active' : 'Disabled'}
                            </span>
                          </div>

                          {/* Internal ID - Always visible in expand to avoid empty area if table is fully visible */}
                          <div className="add-department-for-employee-expanded-item add-department-for-employee-exp-full">
                            <span className="add-department-for-employee-exp-label">Department Internal ID</span>
                            <span className="add-department-for-employee-exp-value" style={{fontFamily: 'monospace', color: '#94a3b8'}}>{dept._id}</span>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AddDepartment;